import type { ReportQueryRequest } from '@/api/types';
import type { DeliveryQuery, MarkupQuery } from '@/api/resources';
import {
  defaultPerformanceReportDateRange,
} from '@/lib/performance-report-search';
import {
  fmtDateInput,
  nyDateInputToMs,
  nyEndOfDayIso,
  nyStartOfDayIso,
} from '@/lib/datetime';

export const DELIVERY_REPORT_TABS = [
  'overview',
  'campaign',
  'production',
  'channel',
  'audience',
  'audienceType',
] as const;

export const MARKUP_REPORT_TABS = [
  'overview',
  'campaign',
  'channel',
  'production',
] as const;

export const OPERATIONAL_REPORT_CHANNELS = ['meta', 'ctv', 'dsp'] as const;

export type DeliveryReportTab = (typeof DELIVERY_REPORT_TABS)[number];
export type MarkupReportTab = (typeof MARKUP_REPORT_TABS)[number];
export type OperationalReportChannel = (typeof OPERATIONAL_REPORT_CHANNELS)[number];

interface OperationalReportSearch<TTab extends string> {
  tab: TTab;
  /** Inclusive America/New_York calendar days. */
  from: string;
  to: string;
  scopeId?: string;
  channel: OperationalReportChannel;
}

export type DeliveryReportSearch = OperationalReportSearch<DeliveryReportTab>;
export type MarkupReportSearch = OperationalReportSearch<MarkupReportTab>;
export type DeliveryReportSearchPatch = Partial<DeliveryReportSearch>;
export type MarkupReportSearchPatch = Partial<MarkupReportSearch>;

const DAY_MS = 86_400_000;
const MAX_RANGE_DAYS = 366;
const SAFE_SCOPE = /^[A-Za-z0-9_:/-]{1,180}$/;

export function parseDeliveryReportSearch(
  search: object,
  now = Date.now(),
): DeliveryReportSearch {
  return parseOperationalSearch(
    search,
    DELIVERY_REPORT_TABS,
    'overview',
    now,
  );
}

export function parseMarkupReportSearch(
  search: object,
  now = Date.now(),
): MarkupReportSearch {
  return parseOperationalSearch(
    search,
    MARKUP_REPORT_TABS,
    'overview',
    now,
  );
}

export function updateDeliveryReportSearch(
  current: DeliveryReportSearch,
  patch: DeliveryReportSearchPatch,
): DeliveryReportSearch {
  return updateOperationalSearch(current, patch, DELIVERY_REPORT_TABS);
}

export function updateMarkupReportSearch(
  current: MarkupReportSearch,
  patch: MarkupReportSearchPatch,
): MarkupReportSearch {
  return updateOperationalSearch(current, patch, MARKUP_REPORT_TABS);
}

/** Explicit defaults make copied URLs and back/forward behavior reproducible. */
export function serializeOperationalReportSearch<TTab extends string>(
  search: OperationalReportSearch<TTab>,
): OperationalReportSearch<TTab> {
  return { ...search };
}

export function operationalReportDateQuery(
  search: Pick<DeliveryReportSearch, 'from' | 'to'>,
): { from: string; to: string } {
  return {
    from: nyStartOfDayIso(nyDateInputToMs(search.from)),
    to: nyEndOfDayIso(nyDateInputToMs(search.to)),
  };
}

export function deliveryPlacementRequest(search: DeliveryReportSearch): DeliveryQuery {
  const audience = deliveryAudienceScope(search);
  return {
    ...operationalReportDateQuery(search),
    tab: search.tab === 'audienceType' ? 'audience_type' : search.tab,
    scope_id: search.tab === 'channel'
      ? search.channel
      : search.tab === 'audience'
        ? audience.audienceId
        : search.scopeId,
    audience_revision: audience.audienceRevision,
    channel: search.tab === 'channel' ? search.channel : undefined,
  };
}

export function markupReportRequest(search: MarkupReportSearch): MarkupQuery {
  return {
    ...operationalReportDateQuery(search),
    ...(search.tab === 'campaign' && search.scopeId
      ? { campaign_id: search.scopeId }
      : search.tab === 'production' && search.scopeId
        ? { production_id: search.scopeId }
        : search.tab === 'channel'
          ? { channel: search.channel }
          : {}),
  };
}

export function operationalFreshnessRequest(
  search: DeliveryReportSearch | MarkupReportSearch,
): ReportQueryRequest {
  const isDelivery = DELIVERY_REPORT_TABS.includes(search.tab as DeliveryReportTab);
  const audience = isDelivery
    ? deliveryAudienceScope(search as DeliveryReportSearch)
    : {};
  return {
    dataset: 'delivery',
    date_from: search.from,
    date_to: search.to,
    group_by: [],
    filters: {
      assigned_production_ids: search.tab === 'production' && search.scopeId
        ? [search.scopeId]
        : [],
      sold_production_ids: [],
      campaign_ids: search.tab === 'campaign' && search.scopeId
        ? [search.scopeId]
        : [],
      audience_ids: search.tab === 'audience' && audience.audienceId
        ? [audience.audienceId]
        : [],
      audience_revision: search.tab === 'audience'
        ? audience.audienceRevision ?? null
        : null,
      creative_ids: [],
      channels: search.tab === 'channel' ? [search.channel] : [],
      audience_types: search.tab === 'audienceType' && search.scopeId
        ? [search.scopeId as ReportQueryRequest['filters']['audience_types'][number]]
        : [],
      targets: [],
      placements: [],
      currencies: ['USD'],
      provider_ad_ids: [],
      provider_creative_ids: [],
      attribution_effects: [],
      attribution_settings: [],
    },
    order: null,
    cursor: null,
    limit: 1,
  };
}

export function deliveryAudienceScope(search: DeliveryReportSearch): {
  audienceId?: string;
  audienceRevision?: number;
} {
  if (search.tab !== 'audience' || !search.scopeId) return {};
  const separator = search.scopeId.lastIndexOf(':');
  const revision = Number(search.scopeId.slice(separator + 1));
  if (separator <= 0 || !Number.isInteger(revision) || revision <= 0) return {};
  return {
    audienceId: search.scopeId.slice(0, separator),
    audienceRevision: revision,
  };
}

function parseOperationalSearch<const TTabs extends readonly string[]>(
  search: object,
  tabs: TTabs,
  fallbackTab: TTabs[number],
  now: number,
): OperationalReportSearch<TTabs[number]> {
  const raw = search as Record<string, unknown>;
  const fallbackRange = defaultPerformanceReportDateRange(now);
  const range = parseDateRange(raw.from, raw.to) ?? fallbackRange;
  return {
    tab: parseEnum(raw.tab, tabs, fallbackTab),
    ...range,
    scopeId: parseScope(raw.scopeId ?? raw.scope),
    channel: parseEnum(raw.channel, OPERATIONAL_REPORT_CHANNELS, 'meta'),
  };
}

function updateOperationalSearch<TTab extends string, const TTabs extends readonly TTab[]>(
  current: OperationalReportSearch<TTab>,
  patch: Partial<OperationalReportSearch<TTab>>,
  tabs: TTabs,
): OperationalReportSearch<TTab> {
  const candidate = { ...current, ...patch };
  const range = parseDateRange(candidate.from, candidate.to) ?? {
    from: current.from,
    to: current.to,
  };
  return {
    tab: parseEnum(candidate.tab, tabs, current.tab),
    ...range,
    scopeId: parseScope(candidate.scopeId),
    channel: parseEnum(candidate.channel, OPERATIONAL_REPORT_CHANNELS, current.channel),
  };
}

function parseDateRange(from: unknown, to: unknown): { from: string; to: string } | undefined {
  if (typeof from !== 'string' || typeof to !== 'string') return undefined;
  const fromMs = nyDateInputToMs(from);
  const toMs = nyDateInputToMs(to);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || fromMs > toMs) return undefined;
  if (fmtDateInput(fromMs) !== from || fmtDateInput(toMs) !== to) return undefined;
  const inclusiveDays = Math.round((toMs - fromMs) / DAY_MS) + 1;
  return inclusiveDays <= MAX_RANGE_DAYS ? { from, to } : undefined;
}

function parseScope(value: unknown): string | undefined {
  return typeof value === 'string' && SAFE_SCOPE.test(value) ? value : undefined;
}

function parseEnum<const T extends readonly string[]>(
  value: unknown,
  values: T,
  fallback: T[number],
): T[number] {
  return typeof value === 'string' && values.includes(value)
    ? value as T[number]
    : fallback;
}
